import React, { useEffect, useState } from "react";
import { walletApi } from "../services/api";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const Wallet = () => {
  const [walletBalance, setWalletBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [referralCode, setReferralCode] = useState("");
  const [referralStats, setReferralStats] = useState({
    totalReferred: 0,
    totalEarnings: 0,
    referredBy: null,
    hasUsedReferral: false
  });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    loadWalletData();

    const reloadOnFocus = () => {
      loadWalletData();
    };

    window.addEventListener("focus", reloadOnFocus);

    return () => {
      window.removeEventListener("focus", reloadOnFocus);
    };
  }, []);

  const loadWalletData = async () => {
    try {
      setLoading(true);
      const [balanceRes, transRes, refRes] = await Promise.all([
        walletApi.getBalance(),
        walletApi.getTransactions(),
        walletApi.getReferralCode()
      ]);

      setWalletBalance(balanceRes?.data?.balance ?? 0);
      setTransactions(transRes?.data?.transactions || []);

      const referralData = refRes?.data || {};
      setReferralCode(referralData.referralCode || "");
      setReferralStats({
        totalReferred: referralData.totalReferred ?? 0,
        totalEarnings: referralData.totalEarnings ?? 0,
        referredBy: referralData.referredBy || null,
        hasUsedReferral: referralData.hasUsedReferral || false
      });
    } catch (err) {
      setMessage({
        type: "error",
        text: "Failed to load wallet data"
      });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = async () => {
    if (!referralCode) return;
    try {
      await navigator.clipboard.writeText(referralCode);
      setMessage({ type: "success", text: "Referral code copied to clipboard!" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg,#fff7f5 0%, #f8ece8 100%)",
        py: 6,
        px: 2
      }}
    >
      <Box display="flex" justifyContent="center">
        <Box sx={{ width: "100%", maxWidth: 1000 }}>

          {/* MESSAGE ALERTS */}
          {message.text && (
            <Alert
              severity={message.type}
              sx={{ mb: 3 }}
              onClose={() => setMessage({ type: "", text: "" })}
            >
              {message.text}
            </Alert>
          )}

          {/* HEADER CARD */}
          <Card
            sx={{
              background: "linear-gradient(135deg,#5c0000 0%, #8B0000 100%)",
              color: "#fff",
              borderRadius: "30px",
              overflow: "hidden",
              mb: 4,
              boxShadow: "0 20px 50px rgba(139,0,0,0.12)"
            }}
          >
            <CardContent sx={{ p: 5 }}>
              <Typography
                variant="h4"
                fontWeight={700}
                mb={1}
              >
                💰 Wallet & Referral
              </Typography>
              <Typography sx={{ opacity: 0.85 }}>
                Manage your wallet balance and referral earnings
              </Typography>
            </CardContent>
          </Card>

          {/* STATS SECTION */}
          <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr 1fr" }} gap={3} mb={4}>
            <Card sx={{ borderRadius: "20px", boxShadow: "0 10px 30px rgba(139,0,0,0.1)" }}>
              <CardContent>
                <Typography variant="body2" sx={{ color: "#7b7b7b", mb: 1 }}>
                  Wallet Balance
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#2b2b2b" }}>
                  ₹{walletBalance?.toFixed(2) ?? "0.00"}
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: "20px", boxShadow: "0 10px 30px rgba(139,0,0,0.1)" }}>
              <CardContent>
                <Typography variant="body2" sx={{ color: "#7b7b7b", mb: 1 }}>
                  Referred Users
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#2b2b2b" }}>
                  {referralStats.totalReferred}
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: "20px", boxShadow: "0 10px 30px rgba(139,0,0,0.1)" }}>
              <CardContent>
                <Typography variant="body2" sx={{ color: "#7b7b7b", mb: 1 }}>
                  Referral Earnings
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#2b2b2b" }}>
                  ₹{referralStats.totalEarnings?.toFixed(2) ?? "0.00"}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* REFERRAL STATUS SECTION */}
          {referralStats.hasUsedReferral && (
            <Card sx={{ borderRadius: "20px", mb: 4, boxShadow: "0 10px 30px rgba(139,0,0,0.1)", background: "#e8f5e9" }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="body1" sx={{ color: "#2e7d32", fontWeight: 700 }}>
                  ✅ You joined via a referral code and received ₹100 bonus!
                </Typography>
                <Typography variant="body2" sx={{ color: "#558b2f", mt: 1 }}>
                  Your referrer earned ₹100 for bringing you to our platform. Thank you for being part of our community!
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* REFERRAL CODE SECTION */}
          <Card sx={{ borderRadius: "20px", mb: 4, boxShadow: "0 10px 30px rgba(139,0,0,0.1)" }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" fontWeight={700} mb={3} sx={{ color: "#5c0000" }}>
                Share Your Referral Code
              </Typography>

              <Box
                display="grid"
                gridTemplateColumns={{ xs: "1fr", sm: "2fr auto" }}
                gap={2}
                mb={3}
              >
                <Box
                  sx={{
                    px: 3,
                    py: 2,
                    borderRadius: "16px",
                    background: "#faf5f2",
                    border: "2px solid rgba(139,0,0,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontFamily: "monospace",
                    fontWeight: 700,
                    letterSpacing: "0.1em"
                  }}
                >
                  {referralCode || "REFERRAL CODE"}
                </Box>

                <Button
                  variant="contained"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopyCode}
                  disabled={!referralCode}
                  sx={{
                    borderRadius: "16px",
                    textTransform: "none",
                    background: "linear-gradient(135deg,#5c0000,#8B0000)",
                    "&:hover": {
                      background: "linear-gradient(135deg,#470000,#700000)"
                    }
                  }}
                >
                  Copy
                </Button>
              </Box>

              <Typography variant="body2" sx={{ color: "#7b7b7b" }}>
                Share this code with friends to earn ₹100 for each signup using your code!
              </Typography>
            </CardContent>
          </Card>

          {/* TRANSACTIONS TABLE */}
          <Card sx={{ borderRadius: "20px", boxShadow: "0 10px 30px rgba(139,0,0,0.1)" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} mb={3} sx={{ color: "#5c0000" }}>
                Transaction History
              </Typography>

              {transactions.length === 0 ? (
                <Typography sx={{ textAlign: "center", color: "#999", py: 4 }}>
                  No transactions yet
                </Typography>
              ) : (
                <TableContainer component={Paper} sx={{ borderRadius: "16px" }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ background: "#f5f5f5" }}>
                        <TableCell sx={{ fontWeight: 700, color: "#5c0000" }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#5c0000" }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#5c0000" }}>Amount</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#5c0000" }}>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map((tx, idx) => (
                        <TableRow key={idx} sx={{ "&:hover": { background: "#fafafa" } }}>
                          <TableCell sx={{ fontSize: "0.9rem" }}>
                            {new Date(tx.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.9rem" }}>
                            <Box
                              sx={{
                                px: 1.5,
                                py: 0.5,
                                borderRadius: "8px",
                                display: "inline-block",
                                background: tx.type === "credit" ? "#e8f5e9" : "#ffebee",
                                color: tx.type === "credit" ? "#2e7d32" : "#c62828",
                                fontWeight: 700,
                                fontSize: "0.85rem"
                              }}
                            >
                              {tx.type === "credit" ? "+ Credit" : "- Debit"}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.9rem", fontWeight: 700, color: tx.type === "credit" ? "#2e7d32" : "#c62828" }}>
                            ₹{tx.amount?.toFixed(2) ?? "0.00"}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.9rem" }}>
                            {tx.reason}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

        </Box>
      </Box>
    </Box>
  );
};

export default Wallet;
